
import { t } from "../lib/i18n";
/**
 * 免税事業者等からの仕入れに係る仕入税額控除の経過措置。
 *
 * 適格請求書発行事業者として未登録の仕入先（免税事業者等）からの仕入れは、
 * 経過措置により支払消費税の一部だけが仕入税額控除の対象になる。控除割合は
 * 取引日（発注書の発行日）で決まり、段階的に引き下げられる:
 *   - 2023-10-01 〜 2026-09-30 … 80%
 *   - 2026-10-01 〜 2029-09-30 … 50%
 *   - 2029-10-01 〜            … 控除不可（0%）
 *
 * 登録済みの仕入先には適用されない（全額控除）。
 */

/** 80% 控除期間の開始日（この日以降・50%開始前は 80%）。 */
export const TRANSITION_50_START = "2026-10-01";
/** 50% 控除期間の開始日。 */
export const TRANSITION_END = "2029-10-01";

export interface TransitionalMeasure {
  /** 未登録仕入先からの支払消費税に対する控除割合 (0〜1)。 */
  rate: number;
  /** 表示用の控除割合（百分率, 80/50/0）。 */
  ratePercent: number;
  /** 次に控除割合が引き下げられる予定（最終段階なら undefined）。 */
  next?: { ratePercent: number; startDate: string };
}

/**
 * 発注書の発行日（YYYY-MM-DD）から適用される経過措置の控除割合を返す。
 * ISO日付は辞書順比較で日付順になるため文字列比較で判定する。
 */
export function transitionalMeasure(issueDateIso: string): TransitionalMeasure {
  if (issueDateIso >= TRANSITION_END) {
    return { rate: 0, ratePercent: 0 };
  }
  if (issueDateIso >= TRANSITION_50_START) {
    return {
      rate: 0.5,
      ratePercent: 50,
      next: { ratePercent: 0, startDate: TRANSITION_END },
    };
  }
  return {
    rate: 0.8,
    ratePercent: 80,
    next: { ratePercent: 50, startDate: TRANSITION_50_START },
  };
}

export interface DeductibleSplit {
  /** 仕入税額控除の対象となる消費税額。 */
  deductible: number;
  /** 経過措置により控除できない消費税額。 */
  nonDeductible: number;
}

/** 支払消費税額を控除割合で「控除可能／控除不可」に按分する（円未満四捨五入）。 */
export function deductibleSplit(totalTax: number, rate: number): DeductibleSplit {
  const deductible = Math.round(totalTax * rate);
  return { deductible, nonDeductible: totalTax - deductible };
}

export interface TransitionalSummary {
  measure: TransitionalMeasure;
  /** 未登録仕入先への支払消費税の総額（軽減税＋標準税）。 */
  totalTax: number;
  split: DeductibleSplit;
}

/**
 * 未登録仕入先の発注書について、発行日と支払消費税総額から経過措置の要約を返す。
 * 控除割合の判定・按分を一箇所に集約し、画面バナー・PDF・CSV で共有する。
 */
export function transitionalSummary(
  totalTax: number,
  issueDateIso: string,
): TransitionalSummary {
  const measure = transitionalMeasure(issueDateIso);
  return { measure, totalTax, split: deductibleSplit(totalTax, measure.rate) };
}

/**
 * ISO日付(YYYY-MM-DD)を和風表記(YYYY年M月D日)へ。Date を使わず文字列で整形。
 * **画面表示用**＝UI ロケール駆動（en では EN 辞書の形になる）。
 * 帳票（PDF/CSV）には使わない → `formatJpDateFixed`。
 */
export function formatJpDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  return t("{year}年{month}月{day}日", {
    year,
    month: Number(month),
    day: Number(day),
  });
}

/**
 * 和風表記（YYYY年M月D日）**固定**＝仕入先向けの帳票（PDF/CSV）用（Step 3）。
 *
 * 帳票はリクエスト外へ出ていくもの＝日付の形は**店の国**で決める（UI ロケールでは
 * ない＝日本の店のオーナーが英語UIで操作しても、仕入先向け書類の和風日付は変えない）。
 * 和風日付が載る節（適格請求書の経過措置）は JP の店の帳票にしか描画されない
 * （`showInvoiceTransitionNote` の国ゲート）ため、帳票側は常に和風固定でよい。
 * 非 JP の店の帳票の日付は ISO（YYYY-MM-DD）のまま＝この関数を通らない。
 * 画面（リクエスト内）の表示は従来どおり `formatJpDate`（UI ロケール駆動）。
 */
export function formatJpDateFixed(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${year}年${Number(month)}月${Number(day)}日`;
}
